<template>
  <el-dialog v-model="visible" title="LOT 상세" width="700px">
    <template v-if="lot">
      <el-descriptions :column="2" border>
        <el-descriptions-item label="LOT번호">{{ lot.lot_number }}</el-descriptions-item>
        <el-descriptions-item label="자재유형">{{ lot.material_type_display }}</el-descriptions-item>
        <el-descriptions-item label="자재명">{{ lot.material_name }}</el-descriptions-item>
        <el-descriptions-item label="상태">
          <el-tag>{{ lot.status_display }}</el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="총수량">{{ lot.quantity }} {{ lot.unit }}</el-descriptions-item>
        <el-descriptions-item label="잔여수량">{{ lot.remaining_quantity }} {{ lot.unit }}</el-descriptions-item>
        <el-descriptions-item label="총금액">{{ lot.currency }} {{ formatNumber(lot.total_amount) }}</el-descriptions-item>
        <el-descriptions-item label="단가">{{ lot.currency }} {{ formatNumber(lot.unit_cost) }}</el-descriptions-item>
        <el-descriptions-item label="공급업체">{{ lot.supplier_name || '-' }}</el-descriptions-item>
        <el-descriptions-item label="보관창고">{{ lot.warehouse_name || '-' }}</el-descriptions-item>
        <el-descriptions-item label="구매발주번호">{{ lot.purchase_order_number || '-' }}</el-descriptions-item>
        <el-descriptions-item label="발주일">{{ lot.order_date }}</el-descriptions-item>
        <el-descriptions-item label="입고일">{{ lot.received_date || '-' }}</el-descriptions-item>
        <el-descriptions-item label="유효기한">{{ lot.expiry_date || '-' }}</el-descriptions-item>
        <el-descriptions-item label="사용률">{{ (lot.usage_percentage || 0).toFixed(1) }}%</el-descriptions-item>
      </el-descriptions>

      <el-divider />

      <h4>배분 내역</h4>
      <el-table :data="usages || []" stripe size="small" v-loading="loadingUsages">
        <el-table-column prop="main_request_number" label="메인오더" width="140" />
        <el-table-column prop="quantity" label="수량" width="100" align="right">
          <template #default="{ row }">
            {{ row.quantity }} {{ lot.unit }}
          </template>
        </el-table-column>
        <el-table-column label="금액" width="120" align="right">
          <template #default="{ row }">
            {{ lot.currency }} {{ formatNumber(row.total_cost) }}
          </template>
        </el-table-column>
        <el-table-column prop="allocation_date" label="배분일" width="100" />
        <el-table-column prop="remarks" label="비고" />
      </el-table>
      <el-empty v-if="usages.length === 0" description="배분 내역이 없습니다" :image-size="60" />

      <el-divider />

      <h4>이동 이력</h4>
      <el-table :data="transfers || []" stripe size="small" v-loading="loadingTransfers">
        <el-table-column label="유형" width="100">
          <template #default="{ row }">
            <el-tag size="small">{{ row.transfer_type_display }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="from_warehouse_name" label="출발" width="120" />
        <el-table-column prop="to_warehouse_name" label="도착" width="120" />
        <el-table-column prop="quantity" label="수량" width="100" align="right">
          <template #default="{ row }">
            {{ formatNumber(row.quantity) }} {{ lot.unit }}
          </template>
        </el-table-column>
        <el-table-column prop="transfer_date" label="이동일" width="100" />
        <el-table-column label="상태" width="90" align="center">
          <template #default="{ row }">
            <el-tag :type="getTransferStatusType(row.status)" size="small">
              {{ row.status_display }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="remarks" label="비고" />
      </el-table>
      <el-empty v-if="transfers.length === 0" description="이동 이력이 없습니다" :image-size="60" />
    </template>

    <template #footer>
      <el-button @click="visible = false">닫기</el-button>
    </template>
  </el-dialog>
</template>

<script setup>
import { ref, watch } from 'vue'
import axiosInstance from '@/api/axios'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  lot: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['update:modelValue'])

const visible = ref(props.modelValue)
const usages = ref([])
const transfers = ref([])
const loadingUsages = ref(false)
const loadingTransfers = ref(false)

const formatNumber = (num) => {
  return parseFloat(num || 0).toLocaleString()
}

const getTransferStatusType = (status) => {
  const types = {
    REQUESTED: 'info',
    IN_TRANSIT: 'warning',
    COMPLETED: 'success',
    CANCELLED: 'danger',
  }
  return types[status] || 'info'
}

const fetchUsages = async () => {
  if (!props.lot?.id) return

  loadingUsages.value = true
  try {
    const response = await axiosInstance.get(`material-lots/${props.lot.id}/usages/`)
    usages.value = response.data
  } catch (error) {
    console.error('Failed to fetch usages:', error)
  } finally {
    loadingUsages.value = false
  }
}

const fetchTransfers = async () => {
  if (!props.lot?.id) return

  loadingTransfers.value = true
  try {
    const response = await axiosInstance.get('lot-transfers/', { params: { lot: props.lot.id } })
    transfers.value = response.data.results || response.data
  } catch (error) {
    console.error('Failed to fetch transfers:', error)
  } finally {
    loadingTransfers.value = false
  }
}

watch(() => props.modelValue, (val) => {
  visible.value = val
  if (val && props.lot) {
    fetchUsages()
    fetchTransfers()
  }
})

watch(visible, (val) => {
  emit('update:modelValue', val)
})
</script>

<style scoped>
h4 {
  margin-bottom: 15px;
}
</style>
